import React, { useEffect, useState } from "react";
import {
    Button, Modal, Form, Table, Alert, Card, InputGroup, Row, Col, Spinner, Badge, Nav, Tab
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPaperPlane, faSearch, faCheckSquare, faCommentDots,
    faInbox, faUserPlus, faCalendarCheck, faCheckDouble, faFilter
} from "@fortawesome/free-solid-svg-icons";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import "moment/locale/pt"; // Importar português para o moment
import "../../../scss/belts.css";

moment.locale('pt');

export default function MensagensAdmin() {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    const authHeaders = () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" });

    // --- ESTADOS GERAIS ---
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [currentUserId, setCurrentUserId] = useState(null);

    // --- ESTADOS: ENVIO DE SMS ---
    const [praticantes, setPraticantes] = useState([]);
    const [turmas, setTurmas] = useState([]);
    const [turmasPraticantes, setTurmasPraticantes] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filtroTurma, setFiltroTurma] = useState("todos");
    const [filtroGraduacao, setFiltroGraduacao] = useState("todos");
    const [selectedIds, setSelectedIds] = useState([]);
    const [messageText, setMessageText] = useState("");
    const [showMessageModal, setShowMessageModal] = useState(false);

    // --- ESTADOS: HISTÓRICO/ALERTAS ---
    const [notificacoes, setNotificacoes] = useState([]);
    const [filtroTipoAlerta, setFiltroTipoAlerta] = useState("todos");

    useEffect(() => {
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setCurrentUserId(decoded["id"]);
            } catch (e) { console.error("Erro token", e); }
        }
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [resP, resT, resTP, resM] = await Promise.all([
                fetch(`${API_BASE_URL}/Praticantes`, { headers: authHeaders() }),
                fetch(`${API_BASE_URL}/Turmas`, { headers: authHeaders() }),
                fetch(`${API_BASE_URL}/TurmasPraticantes`, { headers: authHeaders() }),
                fetch(`${API_BASE_URL}/Mensagens`, { headers: authHeaders() })
            ]);
            setPraticantes(resP.ok ? await resP.json() : []);
            setTurmas(resT.ok ? await resT.json() : []);
            setTurmasPraticantes(resTP.ok ? await resTP.json() : []);
            setNotificacoes(resM.ok ? await resM.json() : []);
        } catch (err) { setError("Erro ao carregar dados."); }
        finally { setLoading(false); }
    };

    // --- TRATAMENTO DE NOMES DE TIPO ---
    const formatTipoAlerta = (tipo) => {
        const mapeamento = {
            "NotificacaoNovoPraticante": "Novo Praticante",
            "NotificacaoConfirmacaoEvento": "Confirmação de Evento",
            "NotificacaoEvento": "Evento Geral"
        };
        return mapeamento[tipo] || tipo.replace("Notificacao", "");
    };

    // --- LÓGICA FILTRAGEM: ENVIO SMS ---
    const praticantesFiltrados = praticantes.filter(p => {
        const matchesSearch = p.nome.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTurma = filtroTurma === "todos" || turmasPraticantes.some(tp => tp.praticanteId === p.id && tp.turmaId === parseInt(filtroTurma));
        let matchesGrad = filtroGraduacao === "todos" || (filtroGraduacao === "vermelhos_graduados" && p.graduacao >= 5) || (filtroGraduacao === "graduados" && p.graduacao >= 6) || (filtroGraduacao === "pretos" && p.graduacao >= 9);
        return matchesSearch && matchesTurma && matchesGrad && p.isAtivo === 1;
    });

    // --- LÓGICA FILTRAGEM: ALERTAS RECEBIDOS ---
    const alertasStaff = (Array.isArray(notificacoes) ? notificacoes : [])
        .filter(n => {
            const isStaffType = (n.tipo === "NotificacaoConfirmacaoEvento" || n.tipo === "NotificacaoNovoPraticante");
            const isForMe = String(n.utilizadorId) === String(currentUserId);
            const matchesTipo = filtroTipoAlerta === "todos" || n.tipo === filtroTipoAlerta;
            return isStaffType && isForMe && matchesTipo;
        })
        .sort((a, b) => new Date(b.dataEnvio) - new Date(a.dataEnvio));

    const markAsRead = async (id) => {
        const res = await fetch(`${API_BASE_URL}/Mensagens/${id}/ler`, { method: "PATCH", headers: authHeaders() });
        if (res.ok) setNotificacoes(prev => prev.map(n => n.id === id ? { ...n, isLida: true } : n));
    };

    const enviarSms = async () => {
        const destinatarios = praticantes.filter(p => selectedIds.includes(p.id)).map(p => p.telefone?.toString()).filter(t => t?.length >= 9);
        const res = await fetch(`${API_BASE_URL}/Mensagens/enviar-sms`, {
            method: "POST",
            headers: authHeaders(),
            body: JSON.stringify({ telemoveis: destinatarios, mensagem: messageText }),
        });
        if (res.ok) {
            setSuccess("SMS enviado com sucesso!");
            setShowMessageModal(false);
            setSelectedIds([]);
            setMessageText("");
        }
    };

    return (
        <>
            <div className="py-4">
                <h1 className="h4 mb-1">Centro de Mensagens</h1>
                <p className="mb-0 text-muted">Envie SMS e consulte as notificações de sistema.</p>
            </div>

            {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible>{success}</Alert>}
            {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}

            <Tab.Container defaultActiveKey="enviar">
                <Card border="light" className="shadow-sm">
                    <Card.Header className="border-bottom border-light pb-0 px-3">
                        <Nav variant="tabs" className="mb-n1">
                            <Nav.Item>
                                <Nav.Link eventKey="enviar" className="fw-bold">
                                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" /> Enviar Mensagens
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="recebidos" className="fw-bold d-flex align-items-center">
                                    <FontAwesomeIcon icon={faInbox} className="me-2" />
                                    Alertas Recebidos
                                    {alertasStaff.filter(n => !n.isLida).length > 0 && (
                                        <Badge bg="danger" className="ms-2">{alertasStaff.filter(n => !n.isLida).length}</Badge>
                                    )}
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Card.Header>

                    <Tab.Content>
                        {/* SEPARADOR: ENVIAR SMS */}
                        <Tab.Pane eventKey="enviar" className="py-4 px-3">
                            <Row className="mb-3 g-2">
                                <Col xs={12} md={4}><Form.Control placeholder="Pesquisar praticante..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} /></Col>
                                <Col xs={6} md={3}>
                                    <Form.Select value={filtroTurma} onChange={(e) => setFiltroTurma(e.target.value)}>
                                        <option value="todos">Todas as Turmas</option>
                                        {turmas.map(t => <option key={t.id} value={t.id}>{t.turma}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col xs={6} md={3}>
                                    <Form.Select value={filtroGraduacao} onChange={(e) => setFiltroGraduacao(e.target.value)}>
                                        <option value="todos">Todas as Graduações</option>
                                        <option value="vermelhos_graduados">Cintos Vermelhos +</option>
                                        <option value="graduados">Castanhos + Pretos</option>
                                    </Form.Select>
                                </Col>
                                <Col xs={12} md={2}>
                                    <Button variant="primary" className="w-100" disabled={selectedIds.length === 0} onClick={() => setShowMessageModal(true)}>
                                        Enviar ({selectedIds.length})
                                    </Button>
                                </Col>
                            </Row>
                            <Table responsive hover className="align-items-center mb-0">
                                <thead className="thead-light">
                                <tr>
                                    <th style={{ width: '5%' }}>
                                        <Form.Check onChange={() => {
                                            const ids = praticantesFiltrados.map(p => p.id);
                                            setSelectedIds(selectedIds.length === ids.length ? [] : ids);
                                        }} checked={selectedIds.length === praticantesFiltrados.length && praticantesFiltrados.length > 0} />
                                    </th>
                                    <th>Nome</th>
                                    <th>Telemóvel</th>
                                    <th className="text-end">Ações</th>
                                </tr>
                                </thead>
                                <tbody>
                                {praticantesFiltrados.map(p => (
                                    <tr key={p.id}>
                                        <td><Form.Check checked={selectedIds.includes(p.id)} onChange={() => setSelectedIds(prev => prev.includes(p.id) ? prev.filter(i => i !== p.id) : [...prev, p.id])} /></td>
                                        <td className="fw-bold text-dark">{p.nome}</td>
                                        <td>{p.telefone || <small className="text-danger italic">Sem contacto</small>}</td>
                                        <td className="text-end">
                                            <Button variant="link" size="sm" className="text-success" onClick={() => { setSelectedIds([p.id]); setShowMessageModal(true); }} disabled={!p.telefone}><FontAwesomeIcon icon={faCommentDots}/></Button>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                        </Tab.Pane>

                        {/* SEPARADOR: ALERTAS RECEBIDOS */}
                        <Tab.Pane eventKey="recebidos" className="py-4 px-3">
                            {/* Filtro por Tipo de Alerta */}
                            <Row className="mb-3">
                                <Col xs={12} md={4} lg={3}>
                                    <InputGroup size="sm">
                                        <InputGroup.Text className="bg-white border-end-0 text-muted"><FontAwesomeIcon icon={faFilter} /></InputGroup.Text>
                                        <Form.Select
                                            className="border-start-0 ps-0 fw-bold text-primary"
                                            value={filtroTipoAlerta}
                                            onChange={(e) => setFiltroTipoAlerta(e.target.value)}
                                        >
                                            <option value="todos">Todos os Tipos</option>
                                            <option value="NotificacaoNovoPraticante">Novos Praticantes</option>
                                            <option value="NotificacaoConfirmacaoEvento">Confirmações de Evento</option>
                                        </Form.Select>
                                    </InputGroup>
                                </Col>
                            </Row>

                            <Table responsive hover className="align-items-center">
                                <thead className="thead-light">
                                <tr>
                                    <th>Tipo de Alerta</th>
                                    <th style={{ width: '45%' }}>Mensagem</th>
                                    <th>Recebido em</th>
                                    <th className="text-end">Estado / Ação</th>
                                </tr>
                                </thead>
                                <tbody>
                                {alertasStaff.map(n => (
                                    <tr key={n.id} className={n.isLida ? "opacity-6" : "bg-soft-light"}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon
                                                    icon={n.tipo === "NotificacaoNovoPraticante" ? faUserPlus : faCalendarCheck}
                                                    className={`me-2 ${n.tipo === "NotificacaoNovoPraticante" ? "text-primary" : "text-warning"}`}
                                                />
                                                <span className="fw-bold">{formatTipoAlerta(n.tipo)}</span>
                                            </div>
                                        </td>
                                        <td><span className={n.isLida ? "text-muted" : "text-dark"}>{n.conteudo}</span></td>
                                        <td><small className="text-muted text-uppercase">{moment(n.dataEnvio).calendar()}</small></td>
                                        <td className="text-end">
                                            {!n.isLida ? (
                                                <Button variant="outline-primary" size="xs" className="fw-bold shadow-none" onClick={() => markAsRead(n.id)}>
                                                    <FontAwesomeIcon icon={faCheckDouble} className="me-1" /> Resolver
                                                </Button>
                                            ) : (
                                                <Badge bg="soft-success" className="text-success text-uppercase">Resolvido</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {alertasStaff.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5 text-muted italic">
                                            Não foram encontrados alertas com o filtro selecionado.
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </Table>
                        </Tab.Pane>
                    </Tab.Content>
                </Card>
            </Tab.Container>

            {/* MODAL SMS */}
            <Modal show={showMessageModal} centered onHide={() => setShowMessageModal(false)}>
                <Modal.Header closeButton className="bg-light"><Modal.Title className="h5">Redigir SMS</Modal.Title></Modal.Header>
                <Modal.Body>
                    <div className="mb-3 p-2 bg-soft-primary rounded text-center small fw-bold">
                        Enviando para {selectedIds.length} contacto(s)
                    </div>
                    <Form.Label className="small fw-bold">Conteúdo da Mensagem</Form.Label>
                    <Form.Control
                        as="textarea" rows={4}
                        placeholder="Escreva aqui a sua mensagem administrativa..."
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="link" size="sm" className="text-gray" onClick={() => setShowMessageModal(false)}>Cancelar</Button>
                    <Button variant="primary" size="sm" onClick={enviarSms} disabled={!messageText.trim()}>Enviar Agora</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}